import { delay } from '.';

export interface RetryOptions {
  baseDelay?: number;
  maxDelay?: number;
  maxAttempts?: number;
  enableJitter?: boolean;
  jitterFactor?: number;
}

export interface RetryContext {
  attemptNumber: number;
  maxAttempts: number;
  backoffDelay: number;
  isRetry: boolean;
}

export type RetryOperation<T> = (context: RetryContext) => Promise<T>;
export type RetrySuccessHandler<T> = (result: T, context: RetryContext) => void;
export type RetryFailureHandler = (context: RetryContext) => void;
export type RetryAttemptHandler = (context: RetryContext) => void;

export class RetryManager {
  private readonly baseDelay: number;
  private readonly maxDelay: number;
  private readonly maxAttempts: number;
  private readonly enableJitter: boolean;
  private readonly jitterFactor: number;

  constructor(options: RetryOptions = {}) {
    this.baseDelay = options.baseDelay ?? 1000;
    this.maxDelay = options.maxDelay ?? 10000;
    this.maxAttempts = options.maxAttempts ?? 3;
    this.enableJitter = options.enableJitter ?? false;
    this.jitterFactor = options.jitterFactor ?? 0.25;
  }

  /**
   * Calculate exponential backoff delay with optional jitter
   */
  private calculateDelay(attemptNumber: number): number {
    const exponentialDelay = Math.min(
      this.baseDelay * Math.pow(2, attemptNumber - 1),
      this.maxDelay
    );

    if (!this.enableJitter) {
      return exponentialDelay;
    }

    const jitter = Math.random() * (exponentialDelay * this.jitterFactor);
    return Math.floor(exponentialDelay + jitter);
  }

  /**
   * Check if retry should continue
   */
  private shouldRetry(attemptNumber: number): boolean {
    return attemptNumber <= this.maxAttempts;
  }

  /**
   * Execute operation with retry logic
   */
  async execute<T>(
    operation: RetryOperation<T>,
    options: {
      onSuccess?: RetrySuccessHandler<T>;
      onFailure?: RetryFailureHandler;
      onAttempt?: RetryAttemptHandler;
    } = {}
  ): Promise<T> {
    const { onSuccess, onFailure, onAttempt } = options;
    let attemptNumber = 1;
    let lastError: unknown;

    while (this.shouldRetry(attemptNumber)) {
      const isRetry = attemptNumber > 1;
      const backoffDelay = isRetry ? this.calculateDelay(attemptNumber) : 0;

      const context: RetryContext = {
        attemptNumber,
        maxAttempts: this.maxAttempts,
        backoffDelay,
        isRetry,
      };

      try {
        // Apply delay for retries
        if (isRetry) {
          await delay(backoffDelay);
        }

        // Call custom attempt handler
        onAttempt?.(context);

        // Execute the operation
        const result = await operation(context);

        // Operation succeeded
        onSuccess?.(result, context);

        return result;
      } catch (error) {
        lastError = error;
        attemptNumber++;

        // Check if we should continue retrying
        if (!this.shouldRetry(attemptNumber)) {
          break;
        }
      }
    }

    // All attempts failed
    const finalContext: RetryContext = {
      attemptNumber: attemptNumber - 1,
      maxAttempts: this.maxAttempts,
      backoffDelay: 0,
      isRetry: true,
    };

    onFailure?.(finalContext);

    throw lastError;
  }

  /**
   * Create a new RetryManager with different options
   */
  static create(options: RetryOptions = {}): RetryManager {
    return new RetryManager(options);
  }

  /**
   * Create a RetryManager with jitter enabled for resilient operations
   */
  static withJitter(baseDelay = 500, maxAttempts = 3): RetryManager {
    return new RetryManager({
      baseDelay,
      maxAttempts,
      enableJitter: true,
      maxDelay: 10000,
    });
  }

  /**
   * Create a RetryManager without jitter for predictable operations
   */
  static withoutJitter(baseDelay = 1000, maxAttempts = 3): RetryManager {
    return new RetryManager({
      baseDelay,
      maxAttempts,
      enableJitter: false,
    });
  }
}
